import { Grid } from '@mui/material';
import ReactTimeline, {
  DateHeader,
  Id,
  SidebarHeader,
  TimelineGroupBase,
  TimelineHeaders,
  TimelineItemBase,
  TimelineMarkers,
  TodayMarker,
} from 'react-calendar-timeline';
import 'react-calendar-timeline/lib/Timeline.css';
import { useState } from 'react';
import { addMonths, startOfDay, startOfToday } from 'date-fns';
import PriorityQueries from '../admin/PriorityQueries';
import { useQuery } from 'react-query';
import { dateToUnix } from '../../utils/Utils';
import { PriorityItemDto } from '../../api';
import '../../styles/timelineStyles.css';
import TimelineLegend from './TimelineLegend';
import UpdatePriorityPopup from '../../components/form/popups/UpdatePriorityPopup';
import { endOfDay } from 'date-fns/esm';
import _ from 'lodash';
import {
  getFilteredPriorities,
  getPriorityTimelineItems,
  getUtilisationTimelineItems,
  groupRenderer,
} from './TimelineUtils';
import { employeeToTimelineGroups } from './TimelineMappers';
import TimelineSideBarHeaderRenderer from './TimelineSideBarHeaderRenderer';
import TimelineItemRenderer from './TimelineItemRenderer';
import UtilisationLegend from './UtilisationLegend';
import TimelineOptions, { ITimelineOptions } from './TimelineOptions';
import FilterOptions, { IFilterOptions } from '../FilterOptions';
import TimelineInstructions from './TimelineInstructions';
import {
  getStoredSelection,
  getStoredProxySelection,
} from '../../components/form/react-hook-form-components/FilterStorage';
import { Status } from '../../components/datagrid/ag-grid/renderStatus';
import SelectedRoles from '../management/employees/roles/SelectedRoles';
import ReassignPriorityPopup from '../../components/form/popups/ReassignPriorityPopup';

declare module 'react-calendar-timeline' {
  export interface TimelineItemBase<DateType> {
    priority?: PriorityItemDto;
    utilisation?: number;
  }
}

export function Timeline() {
  const storageKeyTeam = 'timelineTeam';
  const storageKeyRoles = 'timelineRoles';
  const storageKeyProxy = 'timelineProxy';
  const defaultMonths = 3;
  const priorityStates = [Status.Request, Status.InProgress, Status.LeadOnly, Status.OnHold, Status.Complete];
  const [options, setOptions] = useState<ITimelineOptions>({
    filters: priorityStates,
    orderNameBy: undefined,
    orderUtilisationBy: 'asc',
    months: defaultMonths,
    billable: true,
    nonBillable: true,
    availabilityDate: null,
    availability: 0,
  });

  const [filters, setFilters] = useState<IFilterOptions>({
    team: getStoredSelection(storageKeyTeam),
    roles: getStoredSelection(storageKeyRoles),
    proxy: getStoredProxySelection(storageKeyProxy),
  });

  const defaultStartDate = startOfToday();
  const defaultEndDate = addMonths(defaultStartDate, defaultMonths);
  const { FetchPrioritiesByTeam, mutateUpdatePriority } = PriorityQueries();
  const [startDate, setStartDate] = useState<Date>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date>(defaultEndDate);

  const [visibleStartTime, setVisibleStartTime] = useState<number>(dateToUnix(defaultStartDate));
  const [visibleEndTime, setVisibleEndTime] = useState<number>(dateToUnix(defaultEndDate));
  const {
    data: teamPriorities,
    isFetching,
    isFetched,
    isSuccess,
  } = FetchPrioritiesByTeam(
    filters.proxy,
    filters.team?.value,
    options.availabilityDate ?? startDate,
    endDate,
    SelectedRoles(filters.roles),
    undefined
  );
  const [groups, setGroups] = useState<TimelineGroupBase[]>([]);
  const [openGroups, setOpenGroups] = useState<Id[]>([]);
  const [priorities, setPriorities] = useState<TimelineItemBase<number>[]>([]);
  const [isUpdateFormOpen, setIsUpdateFormOpen] = useState<boolean>(false);
  const [selectedPriority, setSelectedPriority] = useState<PriorityItemDto>();
  const [isRightClickDialogOpen, setIsRightClickDialogOpen] = useState(false);
  const [isDrag, setIsDrag] = useState(false);
  const [newID, setNewID] = useState<number>();

  /** Set groups and priorities when the FetchTeamPriorities query is successful or on refetch */
  useQuery(`${isFetching} ${Object.values(options)}`, () => {
    const updatedVisibleEndTime = dateToUnix(addMonths(visibleStartTime, options.months));

    if (updatedVisibleEndTime !== visibleEndTime) {
      setVisibleDateInterval(visibleStartTime, updatedVisibleEndTime);
    }

    if (isFetched && isSuccess) {
      setGroups(getGroups);
      setPriorities(getPriorities);
    }
  });

  /** Set groups when the length of openGroups changes */
  useQuery(`${openGroups.length}`, () => setGroups(getGroups));

  /**
   * Extracts timeline groups from teamPriorties query data
   * @returns TimelineGroupBase[]
   */
  function getGroups() {
    if (!teamPriorities) return [];

    let groups = teamPriorities.flatMap(employeeToTimelineGroups);

    return groups
      .filter(g => g.root || openGroups.includes(g.parent))
      .filter(g => (options.availability > 0 ? 100 - g.currentUtilisation >= options.availability : g))
      .sort((a, b) => {
        if (options.orderNameBy === 'asc') {
          return a.title.localeCompare(b.title);
        }
        if (options.orderNameBy === 'desc') {
          return b.title.localeCompare(a.title);
        }
        if (options.orderUtilisationBy === 'asc') {
          return a.currentUtilisation - b.currentUtilisation;
        }
        if (options.orderUtilisationBy === 'desc') {
          return b.currentUtilisation - a.currentUtilisation;
        }
        return 0;
      })
      .map(g => groupRenderer(g, openGroups, setOpenGroups));
  }

  /**
   * Extracts timeline items from teamPriorities query data
   * @returns TimelineItemBase<number>[]
   */
  function getPriorities() {
    if (!teamPriorities) return [];

    const priorities = teamPriorities.flatMap(employee => {
      if (!employee.priorities) return [];

      const filteredPriorities = getFilteredPriorities(employee, options);
      const priorityTimelineItems = getPriorityTimelineItems(filteredPriorities);
      const utilisationTimelineItems = getUtilisationTimelineItems(employee, filteredPriorities);

      return priorityTimelineItems.concat(utilisationTimelineItems);
    });

    return priorities;
  }

  /**
   * A function that's called when the user tries to scroll.
   * @param visibleTimeStart start of view port of the calendar
   * @param visibleTimeEnd end of view port of the calendar
   * @param updateScrollCanvas function to update the scroll canvas
   */
  const handleTimeChange = (
    visibleTimeStart: number,
    visibleTimeEnd: number,
    updateScrollCanvas: (start: number, end: number) => void
  ) => {
    setVisibleDateInterval(visibleTimeStart, visibleTimeEnd);
    updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
  };

  /**
   * Handles timeline item movement
   * @param itemId the id of the moved item
   * @param dragTime unix timestamp in milliseconds
   */
  const handleItemMove = async (itemId: number, dragTime: number, newGroupId: Id) => {
    const priority = priorities.filter(item => item.id === itemId);
    setSelectedPriority(priority[0].priority);
    setNewID(newGroupId as number);
    setIsDrag(true);
  };

  /**
   * Handles timeline item resizing
   * @param itemId the id of the resized item
   * @param time unix timestamp in milliseconds
   * @param edge the edge that the item was resized from
   */
  const handleItemResize = (itemId: number, time: number, edge: 'left' | 'right') => {
    const updatedPriorities = priorities.map(item =>
      item.id === itemId
        ? Object.assign({}, item, {
            start_time: edge === 'left' ? time : item.start_time,
            end_time: edge === 'left' ? item.end_time : time,
          })
        : item
    );
    setPriorities(updatedPriorities);
    updateDatabase(itemId, updatedPriorities, [...priorities]);
  };

  /**
   * Updates the database when a priority item has changed. Performs an optimistic update and reverts
   * to the orignal priorities if an error occurs while updating.
   * @param itemId the id of the modified item
   * @param updatedPriorities the updated priorities
   * @param initialPriorities the original priorities
   */
  const updateDatabase = (
    itemId: number,
    updatedPriorities: TimelineItemBase<number>[],
    initialPriorities: TimelineItemBase<number>[]
  ) => {
    const item = updatedPriorities.find(item => item.id === itemId);

    if (item && item.priority) {
      const priority = item.priority;
      const startDate = new Date(item.start_time);
      const endDate = new Date(item.end_time);
      priority.startDate = startDate;
      priority.endDate = endDate;
      priority.employeeID = item.group as number;

      mutateUpdatePriority(priority, {
        onError: () => setPriorities(initialPriorities),
      });
    }
  };

  /**
   * Updates the timeline's visible date interval
   * @param visibleTimeStart start of view port of the calendar
   * @param visibleTimeEnd end of view port of the calendar
   */
  const setVisibleDateInterval = (visibleTimeStart: number, visibleTimeEnd: number) => {
    setVisibleStartTime(visibleTimeStart);
    setVisibleEndTime(visibleTimeEnd);
    setStartDate(startOfDay(visibleTimeStart));
    setEndDate(endOfDay(visibleTimeEnd));
  };

  return (
    <>
      <Grid container direction="row" spacing={4} padding={0.5} justifyContent="center">
        <Grid item xs={12} zIndex={1}>
          <FilterOptions state={filters} setState={setFilters} page={'timeline'} />
        </Grid>

        <Grid item xs={9} sm={9} md={9} lg={9} xl={6}>
          <TimelineOptions state={options} setState={setOptions} />
        </Grid>

        <Grid item container spacing={1} justifyContent="center">
          <Grid item>
            <UtilisationLegend />
          </Grid>

          <Grid item>
            <TimelineLegend
              setFilters={filters => setOptions({ ...options, filters })}
              priorityStates={priorityStates}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} zIndex={0}>
          <TimelineInstructions />
          <ReactTimeline
            groups={groups}
            items={priorities}
            canResize="both"
            canMove={true}
            canChangeGroup={true}
            stackItems={true}
            itemHeightRatio={0.85}
            itemRenderer={props => (
              <TimelineItemRenderer
                itemRendererProps={props}
                onDoubleClick={item => {
                  setSelectedPriority(item.priority);
                  setIsUpdateFormOpen(true);
                }}
                onContextMenu={item => {
                  setSelectedPriority(item.priority);
                  setIsRightClickDialogOpen(true);
                }}
              />
            )}
            onItemResize={handleItemResize}
            onItemMove={handleItemMove}
            onTimeChange={handleTimeChange}
            visibleTimeStart={visibleStartTime}
            visibleTimeEnd={visibleEndTime}
            timeSteps={{
              second: 0,
              minute: 0,
              hour: 0,
              day: 1,
              month: 1,
              year: 1,
            }}
          >
            <TimelineHeaders className="sticky">
              <DateHeader unit="primaryHeader" />
              <DateHeader />
              <SidebarHeader>
                {({ getRootProps }) => {
                  return (
                    <div {...getRootProps()}>
                      <TimelineSideBarHeaderRenderer
                        timelineOptions={options}
                        setTimelineOptions={setOptions}
                        onResetView={() => {
                          setVisibleDateInterval(dateToUnix(defaultStartDate), dateToUnix(defaultEndDate));
                        }}
                      />
                    </div>
                  );
                }}
              </SidebarHeader>
            </TimelineHeaders>

            <TimelineMarkers>
              <TodayMarker date={startOfToday()}>
                {({ styles }) => <div style={{ ...styles, backgroundColor: 'crimson' }} />}
              </TodayMarker>
            </TimelineMarkers>
          </ReactTimeline>
        </Grid>
      </Grid>
      {selectedPriority && (
        <UpdatePriorityPopup isOpen={isUpdateFormOpen} setIsOpen={setIsUpdateFormOpen} priority={selectedPriority} />
      )}
      {selectedPriority && isDrag && (
        <ReassignPriorityPopup
          isOpen={isDrag}
          setIsOpen={setIsDrag}
          priority={selectedPriority}
          onSuccess={() => {
            setIsDrag(false);
            setSelectedPriority(undefined);
          }}
          newID={newID}
        />
      )}
      {selectedPriority && (
        <ReassignPriorityPopup
          isOpen={isRightClickDialogOpen}
          setIsOpen={setIsRightClickDialogOpen}
          priority={selectedPriority}
          onSuccess={() => {
            setIsRightClickDialogOpen(false);
            setSelectedPriority(undefined);
          }}
        />
      )}
    </>
  );
}
